// @flow

// client API for predictor engine

import { ranks, categories, categoricalGradient, StatStructure, ZipsByRank } from '../constants';
import { Granule } from '../../common/types';
import pb from '../pubsub';
const pubsub = pb.setup('storage')

type region = 'csa' | 'msa' | 'state' | 'county'
interface RankJson {
    type: 'ranks' | 'categories' | 'ranked categories',
    data: { [string]: number[] }
}

function encode(stats: { [string]: number }) {
    return Object.keys(stats).map(s => `${s}=${stats[s]}`).join('&')
}

// helper function for re-organizing received data based on color
export function formatRanks(json: RankJson | { message: string }): ?ZipsByRank {
    if (json.message) {
        pubsub.publish('notify:info', json.message)
        return null
    }

    let result = {}
    if (json.type === 'ranks') {
        Object.keys(json.data).forEach((rank, index) => {
            result[ranks[index]] = {
                zips: json.data[rank]
            }
        })
    } else if (json.type === 'versus') {
        let keys = Object.keys(json.data)
        keys.forEach((polarity, index) => {
            let color = polarity === 'no_data' ? '#ffffff' : categoricalGradient[9 - parseInt(polarity)]
            result[color] = {
                versus: json.categories,
                zips: json.data[polarity]
            }
        })
    } else {
        let ranked = json.type == 'ranked categories'
        let keys = Object.keys(json.data)
        // gives cleaner colors for small groups
        let extraStep = Math.floor(ranks.length / (keys.length - 1))
        keys.forEach((category, index) => {
            let color = ranked ? index ? ranks[(index - 1) * extraStep] : '#ffffff' : categories[index]
            result[color] = {
                name: category,
                zips: json.data[category]
            }
        })
    }
    return result
}

// request ranks from the server and organize them after
export function requestRanks(year: number | string, stats: { [string]: number }, region?: { [region]: string }, granule: string = 'zip'): Promise<?ZipsByRank> {
    
    let keys = Object.keys(stats)
    if (!keys.length) {
        return Promise.resolve();
    }
    console.log(region, "CSA")

    if (!region) {
        return fetch(`/api/predict/${year}/rank/us/${granule}?${encode(stats)}`).then(r => r.json()).then(formatRanks)
    } else if (region.state) {
        return fetch(`/api/predict/${year}/rank/state/${region.state.toLocaleLowerCase()}/${granule}?${encode(stats)}`).then(r => r.json()).then(formatRanks)
    } else if (region.msa) {
        return fetch(`/api/predict/${year}/rank/msa/${region.msa}/${granule}?${encode(stats)}`).then(r => r.json()).then(formatRanks)
    } else if (region.csa) {
        return fetch(`/api/predict/${year}/rank/csa/${region.csa}/${granule}?${encode(stats)}`).then(r => r.json()).then(formatRanks)
    } else {
        throw new Error('not implemented')
    }
}

function formatYears(start: number | string, data: ZipsByRank[] | { error: string }) {
    let years = {}
    data.forEach((yearData, i) => {
        years[start + i] = formatRanks(yearData)
    })
    return years
}
function validate(response) {
    return response.json().then(data => {
        if (data.error) {
            throw new Error(data.error)
        } else {
            return data
        }
    })
}
export function requestRanksMultiYear(start: number | string, end: number | string, stats: { [string]: number }, region?: { [region]: string }, granule: granuleSize): Promise<?ZipsByRank[]> {
    if (!Object.keys(stats).length) {
        return Promise.resolve();
    }

    if (!region) {
        return fetch(`/api/predict/${start}-${end}/timelapse/us/${granule}?${encode(stats)}`).then(validate).then(data => formatYears(start, data.years))
    } else if (region.state) {
        return fetch(`/api/predict/${start}-${end}/timelapse/state/${region.state.toLocaleLowerCase()}/${granule}?${encode(stats)}`).then(validate).then(data => formatYears(start, data.years))
    } else if (region.msa) {
        return fetch(`/api/predict/${start}-${end}/timelapse/msa/${region.msa}/${granule}?${encode(stats)}`).then(validate).then(data => formatYears(start, data.years))
    } else if (region.csa) {
        return fetch(`/api/predict/${start}-${end}/timelapse/csa/${region.csa}/${granule}?${encode(stats)}`).then(validate).then(data => formatYears(start, data.years))
    } else {
        throw new Error('not implemented')
    }
}

export function requestDeltaRanksMultiYear(start: number | string, end: number | string, delta: number, stats: { [string]: number }, region?: { [region]: string }, granule: granuleSize): Promise<?ZipsByRank[]> {
    if (!Object.keys(stats).length) {
        return Promise.resolve();
    }

    if (!region) {
        return fetch(`/api/predict/${start + delta}-${end + delta}/timelapse/us/${granule}/delta/${delta}?${encode(stats)}`).then(validate).then(data => formatYears(start, data.years))
    } else if (region.state) {
        return fetch(`/api/predict/${start + delta}-${end + delta}/timelapse/state/${region.state.toLocaleLowerCase()}/${granule}/delta/${delta}?${encode(stats)}`).then(validate).then(data => formatYears(start, data.years))
    } else if (region.msa) {
        return fetch(`/api/predict/${start + delta}-${end + delta}/timelapse/msa/${region.msa}/${granule}/delta/${delta}?${encode(stats)}`).then(validate).then(data => formatYears(start, data.years))
    } else if (region.csa) {
        return fetch(`/api/predict/${start + delta}-${end + delta}/timelapse/csa/${region.csa}/${granule}/delta/${delta}?${encode(stats)}`).then(validate).then(data => formatYears(start, data.years))
    } else {
        throw new Error('not implemented')
    }
}

// request info for specific granule from server and curate it
export function requestInfo(year: number | string, delta: number, stats: { [string]: number }, granule: Granule): Promise<Object> {
    const granuleStr = `${granule.type}/${granule.id}`
    let url = delta ? `/api/predict/${year + delta}/info/${granuleStr}/delta/${delta}?${encode(stats)}` : `/api/predict/${year}/info/${granuleStr}?${encode(stats)}`
    return fetch(url).then(r => r.json()).then(json => {
        let name = '???'
        if (json.city) {
            name = json.city
        } else if (json.county) {
            name = `${json.county} County`
        }
        delete json.city
        delete json.county
        return {
            _name: name,
            ...json
        }
    })
}

// fetch stats structure from the backend
let cachedStats = null
export function requestStats(): Promise<{ [string]: StatStructure }> {
    if (cachedStats) {
        return Promise.resolve(cachedStats)
    } else {
        return fetch(`/api/predict/stats`).then(r => r.json()).then(json => {
            cachedStats = json
            return json
        })
    }
}